import {useState, useMemo, useEffect} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import Select from 'react-select'
import {SEARCH_RESOURCES_CENTER} from '../../gql/queries/resourceCenterQuery'
import {GET_DOCUMENTS_POLICY} from '../../gql/queries/policiesApiQueries'
import {IResourceCenter, IIncrementResourceViewsResponse} from '../../types/resourceCenter'
import {IResAllTagsDocuments} from '../../types/policiesApi'
import {useDisclourse} from '../../hooks/useDisclourse'
import {ModalAddResource} from './components/ModalAddResource'
import {ModalEditResource} from './components/ModalEditResource'
import {ModalDeleteResource} from './components/ModalDeleteResource'
import {ModalViewResource} from './components/ModalViewResource'
import {RenderFilePreview, GridFilePreview} from './components/RenderFilePreview'
import {prependBackendUrl} from '../../gql/client'
import moment from 'moment'
import {
  getResourceTypeIcon,
  getResourceTypeBadgeStyle,
  RESOURCE_TYPE_OPTIONS,
} from './resourceTypeHelpers'
import {INCREMENT_RESOURCE_VIEWS} from '../../gql/mutations/resourceCenterMutations'

interface ISearchResourcesCenterResponse {
  resourcesCenterAdvancedSearch: IResourceCenter[]
}

const ViewsBadge = ({viewsCount, className = ''}: {viewsCount: number; className?: string}) => {
  const baseClass = 'badge bg-primary text-white d-inline-flex align-items-center gap-1'
  return (
    <span className={`${baseClass} ${className}`.trim()}>
      <i className="bi bi-eye" />
      <span>{viewsCount}</span>
    </span>
  )
}

export const ResourceCenterPage = () => {
  const {onOpen: onOpenAdd, isOpen: isOpenAdd, onClose: onCloseAdd} = useDisclourse()
  const {onOpen: onOpenEdit, isOpen: isOpenEdit, onClose: onCloseEdit} = useDisclourse()
  const {onOpen: onOpenDelete, isOpen: isOpenDelete, onClose: onCloseDelete} = useDisclourse()
  const {onOpen: onOpenView, isOpen: isOpenView, onClose: onCloseView} = useDisclourse()

  const [selectedResource, setSelectedResource] = useState<IResourceCenter | null>(null)
  const [filterName, setFilterName] = useState('')
  const [filterType, setFilterType] = useState<string | null>(null)
  const [filterTag, setFilterTag] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table')

  // Debounced filter values - se actualizan 500ms después de que el usuario deje de escribir
  const [debouncedFilterName, setDebouncedFilterName] = useState('')
  const [debouncedFilterTag, setDebouncedFilterTag] = useState<string | null>(null)

  // Debounce effect para filterName
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterName(filterName)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [filterName])

  // Debounce effect para filterTag
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilterTag(filterTag)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [filterTag])

  // Query con filtros debounced que viajan al backend
  const {data, loading, error, refetch} = useQuery<ISearchResourcesCenterResponse>(
    SEARCH_RESOURCES_CENTER,
    {
      variables: {
        name: debouncedFilterName || null,
        resourceType: filterType || null, // filterType no necesita debounce porque es un select
        tagName: debouncedFilterTag || null,
        isActive: true, // Solo recursos activos
      },
      fetchPolicy: 'network-only',
    }
  )

  const {data: dataTags} = useQuery<IResAllTagsDocuments>(GET_DOCUMENTS_POLICY, {
    fetchPolicy: 'cache-first',
  })

  const [incrementResourceViews] = useMutation<
    IIncrementResourceViewsResponse,
    {id: number}
  >(INCREMENT_RESOURCE_VIEWS)

  // Los recursos ya vienen filtrados del backend
  const filteredResources = useMemo(() => {
    return data?.resourcesCenterAdvancedSearch || []
  }, [data])

  // Pagination
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)
  const totalResources = filteredResources.length
  const totalPages = Math.max(1, Math.ceil(totalResources / pageSize))

  useEffect(() => {
    if (page > totalPages) setPage(1)
  }, [filteredResources, pageSize, totalResources, page, totalPages])

  // Paginated resources
  const paginatedResources = useMemo(() => {
    const startIdx = (page - 1) * pageSize
    const endIdx = Math.min(startIdx + pageSize, totalResources)
    return filteredResources.slice(startIdx, endIdx)
  }, [filteredResources, page, pageSize, totalResources])

  const handleEdit = (resource: IResourceCenter) => {
    setSelectedResource(resource)
    onOpenEdit()
  }

  const handleDelete = (resource: IResourceCenter) => {
    setSelectedResource(resource)
    onOpenDelete()
  }

  const handleRefetch = () => {
    refetch()
  }

  const handleSearchClick = () => {
    setDebouncedFilterName(filterName)
    setDebouncedFilterTag(filterTag)
    setPage(1)

    refetch({
      name: filterName || null,
      resourceType: filterType || null,
      tagName: filterTag || null,
      isActive: true,
    })
  }

  const triggerIncrementResourceViews = (resourceId: string) => {
    const numericId = Number(resourceId)

    if (Number.isNaN(numericId)) {
      console.warn('No se pudo incrementar las vistas: id inválido', resourceId)
      return
    }

    incrementResourceViews({variables: {id: numericId}}).catch((mutationError) => {
      console.error('Error incrementando vistas del recurso', mutationError)
    })
  }

  const handleResourceAccess = (resource: IResourceCenter, action: 'view' | 'download' = 'view') => {
    triggerIncrementResourceViews(resource.id)

    if (typeof window === 'undefined') return

    // Si es "view", abrir el modal
    if (action === 'view') {
      setSelectedResource(resource)
      onOpenView()
      return
    }

    // Si es download y tiene file
    if (action === 'download' && resource.file) {
      const resourceUrl = prependBackendUrl(resource.file)
      const anchor = document.createElement('a')
      anchor.href = resourceUrl
      anchor.target = '_blank'
      anchor.rel = 'noopener noreferrer'
      anchor.download = resource.name || 'resource'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
    }
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Error loading resources: {error.message}
      </div>
    )
  }

  return (
    <div className="container-fluid px-4 py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1">Resource Center</h1>
          <p className="text-muted mb-0">
            Manage tutorials, documents, templates, and helpful resources
          </p>
        </div>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')}
            title={`Switch to ${viewMode === 'table' ? 'grid' : 'table'} view`}
          >
            <i className={`bi ${viewMode === 'table' ? 'bi-grid-3x3' : 'bi-list'}`} />
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onOpenAdd}>
            <i className="fa fa-plus me-1" /> Add Resource
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md">
              <label className="form-label fw-bold">Search by name</label>
              <div style={{position: 'relative'}}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Type to search..."
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                />
                <span style={{position: 'absolute', right: '10px', top: '8px'}}>
                  <i className="bi bi-search" />
                </span>
              </div>
            </div>

            <div className="col-12 col-md">
              <label className="form-label fw-bold">Filter by type</label>
              <Select
                isClearable
                options={RESOURCE_TYPE_OPTIONS}
                onChange={(option) => setFilterType(option?.value || null)}
                placeholder="All types"
                styles={{
                  control: (base) => ({...base, padding: '2px'}),
                }}
              />
            </div>

            <div className="col-12 col-md">
              <label className="form-label fw-bold">Filter by tag</label>
              <Select
                isClearable
                options={
                  dataTags?.allAttachmentsTags.edges.map(({node}) => ({
                    label: node.tagName,
                    value: node.tagName,
                  })) || []
                }
                onChange={(option) => setFilterTag(option?.value || null)}
                placeholder="All tags"
                styles={{
                  control: (base) => ({...base, padding: '2px'}),
                }}
              />
            </div>

            <div className="col-12 col-md-auto d-flex align-items-end justify-content-md-end ms-md-auto">
              <button
                className="btn btn-primary btn-sm w-100"
                style={{minWidth: 140}}
                onClick={handleSearchClick}
              >
                <i className="bi bi-search me-2" /> Search
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-3 pt-3 border-top">
            <div className="row text-center">
              <div className="col">
                <h5 className="mb-0">{filteredResources.length}</h5>
                <small className="text-muted">
                  {debouncedFilterName || filterType || debouncedFilterTag
                    ? 'Filtered Results'
                    : 'Total Resources'}
                </small>
              </div>
              {loading && (
                <div className="col">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Searching...</span>
                  </div>
                  <small className="text-muted d-block">Searching...</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'table' ? (
        // Table View
        <div className="card">
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-sm-12 col-lg-6">
                <div className="d-flex align-items-center gap-2">
                  <label className="d-flex align-items-center gap-2 mb-0">
                    Show
                    <select
                      className="form-select form-select-sm"
                      value={pageSize}
                      onChange={(e) => {
                        const newSize = parseInt(e.target.value, 10)
                        setPageSize(newSize === -1 ? totalResources : newSize)
                        setPage(1)
                      }}
                      style={{width: 'auto'}}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={-1}>All</option>
                    </select>
                    entries
                  </label>
                </div>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{width: 50}}></th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Tag</th>
                    <th>Created By</th>
                    <th style={{width: 100}}>Views</th>
                    <th style={{width: 150}}>Created</th>
                    <th style={{width: 150}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedResources.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5">
                        <div className="d-flex flex-column align-items-center">
                          <i className="bi bi-folder2-open" style={{fontSize: 48, color: '#ccc'}} />
                          <h5 className="mt-3 text-muted">No resources found</h5>
                          <p className="text-muted">Try adjusting your filters or add a new resource</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedResources.map((resource) => (
                      <tr key={resource.id}>
                        <td>
                          {resource.url ? (
                            <div style={{width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                              <i className="bi bi-link-45deg fs-3 text-primary" />
                            </div>
                          ) : (
                            <RenderFilePreview file={prependBackendUrl(resource.file || '')} />
                          )}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            <span>{getResourceTypeIcon(resource.resourceType)}</span>
                            <span className="fw-bold">{resource.name}</span>
                          </div>
                          {resource.url && (
                            <small className="text-muted d-block">
                              <i className="bi bi-link-45deg" /> {resource.url}
                            </small>
                          )}
                          {resource.description && (
                            <small className="text-muted d-block">{resource.description}</small>
                          )}
                        </td>
                        <td className="align-middle">
                          <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                            {RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === resource.resourceType)
                              ?.label || resource.resourceType}
                          </span>
                        </td>
                        <td className="align-middle">
                          {resource.documentTag ? (
                            <span className="badge bg-secondary">{resource.documentTag.tagName}</span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="align-middle">
                          {resource.user ? (
                            <div>
                              <div className="fw-semibold">
                                {resource.user.firstName} {resource.user.lastName}
                              </div>
                              <small className="text-muted">
                                {moment(resource.created).format('MMM DD, YYYY')}
                              </small>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="align-middle text-center">
                          <ViewsBadge viewsCount={resource.viewsCount} />
                        </td>
                        <td className="align-middle">
                          <small>{moment(resource.created).fromNow()}</small>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <button
                              onClick={() => handleResourceAccess(resource, 'view')}
                              className="btn btn-sm btn-outline-primary"
                              title="View"
                            >
                              <i className="bi bi-eye" />
                            </button>
                            {!resource.url && (
                              <button
                                onClick={() => handleResourceAccess(resource, 'download')}
                                className="btn btn-sm btn-outline-success"
                                title="Download"
                              >
                                <i className="bi bi-download" />
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-warning"
                              title="Edit"
                              onClick={() => handleEdit(resource)}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              title="Delete"
                              onClick={() => handleDelete(resource)}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="row mt-3">
              <div className="col-sm-12 col-md-5">
                <div className="text-muted">
                  {totalResources === 0
                    ? 'Showing 0 entries'
                    : `Showing ${(page - 1) * pageSize + 1} to ${Math.min(
                        page * pageSize,
                        totalResources
                      )} of ${totalResources} entries`}
                </div>
              </div>
              <div className="col-sm-12 col-md-7">
                <div className="d-flex justify-content-end">
                  <ul className="pagination mb-0">
                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <i className="la la-angle-left" />
                      </button>
                    </li>
                    <li className="page-item">
                      <span className="page-link">
                        Page {page} of {totalPages}
                      </span>
                    </li>
                    <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        <i className="la la-angle-right" />
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Grid View
        <div className="row g-3">
          {loading ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : paginatedResources.length === 0 ? (
            <div className="col-12">
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="bi bi-folder2-open" style={{fontSize: 48, color: '#ccc'}} />
                  <h5 className="mt-3 text-muted">No resources found</h5>
                  <p className="text-muted">Try adjusting your filters or add a new resource</p>
                </div>
              </div>
            </div>
          ) : (
            paginatedResources.map((resource) => (
              <div key={resource.id} className="col-md-6 col-lg-4 col-xl-3">
                <div className="card h-100 shadow-sm hover-shadow">
                  {/* Preview */}
                  <div
                    style={{
                      height: 200,
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    {resource.url ? (
                      <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <i className="bi bi-link-45deg" style={{fontSize: 64, color: '#0d6efd'}} />
                      </div>
                    ) : (
                      <GridFilePreview file={prependBackendUrl(resource.file || '')} />
                    )}

                    {/* Type badge */}
                    <div style={{position: 'absolute', top: 10, left: 10}}>
                      <span style={getResourceTypeBadgeStyle(resource.resourceType)}>
                        {getResourceTypeIcon(resource.resourceType)}{' '}
                        {RESOURCE_TYPE_OPTIONS.find((opt) => opt.value === resource.resourceType)
                          ?.label || resource.resourceType}
                      </span>
                    </div>

                    {/* Views badge */}
                    <div style={{position: 'absolute', top: 10, right: 10}}>
                      <ViewsBadge viewsCount={resource.viewsCount} />
                    </div>
                  </div>

                  <div className="card-body">
                    <h6 className="card-title mb-2">{resource.name}</h6>
                    {resource.description && (
                      <p className="card-text text-muted small" style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {resource.description}
                      </p>
                    )}

                    {resource.documentTag && (
                      <div className="mb-2">
                        <span className="badge bg-secondary">{resource.documentTag.tagName}</span>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center text-muted small">
                      <span>
                        {resource.user
                          ? `${resource.user.firstName} ${resource.user.lastName}`
                          : 'Unknown'}
                      </span>
                      <span>{moment(resource.created).format('MMM DD, YYYY')}</span>
                    </div>
                  </div>

                  <div className="card-footer bg-white border-top-0">
                    <div className="d-flex gap-2">
                      <button
                        onClick={() => handleResourceAccess(resource, 'view')}
                        className="btn btn-sm btn-primary flex-fill"
                      >
                        <i className="bi bi-eye me-1" /> View
                      </button>
                      {!resource.url && (
                        <button
                          onClick={() => handleResourceAccess(resource, 'download')}
                          className="btn btn-sm btn-outline-success"
                          title="Download"
                        >
                          <i className="bi bi-download" />
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => handleEdit(resource)}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(resource)}
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Pagination in grid view */}
          {!loading && paginatedResources.length > 0 && (
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {(page - 1) * pageSize + 1} to{' '}
                      {Math.min(page * pageSize, totalResources)} of {totalResources} entries
                    </div>
                    <ul className="pagination mb-0">
                      <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                        >
                          <i className="la la-angle-left" />
                        </button>
                      </li>
                      <li className="page-item">
                        <span className="page-link">
                          Page {page} of {totalPages}
                        </span>
                      </li>
                      <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page === totalPages}
                        >
                          <i className="la la-angle-right" />
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {isOpenAdd && <ModalAddResource isOpen={isOpenAdd} onClose={onCloseAdd} refetch={handleRefetch} />}

      {isOpenEdit && selectedResource && (
        <ModalEditResource
          isOpen={isOpenEdit}
          onClose={onCloseEdit}
          refetch={handleRefetch}
          resource={selectedResource}
        />
      )}

      {isOpenDelete && selectedResource && (
        <ModalDeleteResource
          isOpen={isOpenDelete}
          onClose={onCloseDelete}
          refetch={handleRefetch}
          resource={selectedResource}
        />
      )}

      {isOpenView && selectedResource && (
        <ModalViewResource
          isOpen={isOpenView}
          onClose={onCloseView}
          resource={selectedResource}
        />
      )}
    </div>
  )
}
